import React from 'react';
import { Box, Button, Chip, Tooltip, Typography } from '@mui/material';
import { updateDoc, doc, arrayUnion } from 'firebase/firestore';
import { db } from 'lib/firebase';

const REACTION_EMOJIS = ['❤️', '👍', '👎'];

/**
 * Shared component for displaying and managing question reactions
 * @param {Object} question - The question object with reactions
 * @param {Object} user - Current user object (null if not logged in)
 * @param {boolean} adminMode - If true, shows compact read-only view for admin tables
 * @param {function} onError - Callback for error handling
 */
const QuestionReactions = ({ question, user, adminMode = false, onError }) => {
  const handleReaction = async (emoji) => {
    if (!user || adminMode) return;

    const questionRef = doc(db, 'weekly_questions', question.id);
    const currentReactions = question.reactions || {};
    const currentEmojiUsers = currentReactions[emoji] || [];
    const hasReacted = currentEmojiUsers.some(u => 
      (typeof u === 'string' ? u === user.uid : u.uid === user.uid)
    );

    try {
      if (hasReacted) {
        // Remove reaction
        const updatedUsers = currentEmojiUsers.filter(u => 
          (typeof u === 'string' ? u !== user.uid : u.uid !== user.uid)
        );
        await updateDoc(questionRef, {
          [`reactions.${emoji}`]: updatedUsers
        });
      } else {
        // Add reaction
        const userReaction = {
          uid: user.uid,
          displayName: user.displayName || 'Family Member',
          photoURL: user.photoURL || null,
          timestamp: new Date().toISOString()
        };
        await updateDoc(questionRef, {
          [`reactions.${emoji}`]: arrayUnion(userReaction)
        });
      }
    } catch (error) {
      if (onError) {
        onError('Failed to update reaction');
      }
    }
  };

  // Admin mode - compact chip display
  if (adminMode) {
    return (
      <Box display="flex" gap={1} flexWrap="wrap">
        {REACTION_EMOJIS.map(emoji => {
          const reactions = question.reactions?.[emoji] || [];
          const count = reactions.length;
          if (count === 0) return null;
          
          const tooltipContent = (
            <Box sx={{ p: 0.5 }}>
              {reactions.map((r, i) => {
                const name = typeof r === 'string' ? 'Family Member' : r.displayName;
                return (
                  <Typography key={i} variant="caption" display="block">
                    {name}
                  </Typography>
                );
              })}
            </Box>
          );

          return (
            <Tooltip key={emoji} title={tooltipContent} arrow>
              <Chip 
                label={`${emoji} ${count}`} 
                size="small" 
                variant="outlined"
                sx={{ borderColor: 'transparent', bgcolor: 'rgba(0,0,0,0.04)' }}
              />
            </Tooltip>
          );
        })}
        {!question.reactions && (
          <Typography variant="caption" color="text.secondary">-</Typography>
        )}
      </Box>
    );
  }

  // User mode - interactive buttons
  return (
    <Box display="flex" gap={1} flexWrap="wrap">
      {REACTION_EMOJIS.map(emoji => {
        const reactions = question.reactions?.[emoji] || [];
        const count = reactions.length;
        const hasReacted = reactions.some(r => 
          (typeof r === 'string' ? r === user?.uid : r.uid === user?.uid)
        );
        
        const tooltipContent = (
          <Box sx={{ p: 1 }}>
            {reactions.length > 0 ? (
              reactions.map((r, i) => {
                const name = typeof r === 'string' ? 'Family Member' : r.displayName;
                return (
                  <Typography key={i} variant="caption" display="block">
                    {name}
                  </Typography>
                );
              })
            ) : (
              <Typography variant="caption">No reactions yet</Typography>
            )}
          </Box>
        );

        return (
          <Tooltip key={emoji} title={tooltipContent} arrow>
            <Button
              size="small"
              variant={hasReacted ? "contained" : "outlined"}
              onClick={() => handleReaction(emoji)}
              disabled={!user}
              sx={{ 
                minWidth: 'auto',
                px: 1.5,
                py: 0.5,
                borderRadius: 4,
                bgcolor: hasReacted ? 'rgba(102, 126, 234, 0.1)' : 'transparent',
                color: hasReacted ? 'primary.main' : 'text.secondary',
                borderColor: hasReacted ? 'primary.main' : 'divider',
                '&:hover': {
                  bgcolor: hasReacted ? 'rgba(102, 126, 234, 0.2)' : 'rgba(0, 0, 0, 0.04)',
                }
              }}
            >
              <span style={{ marginRight: count > 0 ? 6 : 0, fontSize: '1.2rem' }}>{emoji}</span>
              {count > 0 && <span>{count}</span>}
            </Button>
          </Tooltip>
        );
      })}
    </Box>
  );
};

export default QuestionReactions;
